import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPageUrl } from '@/utils';
import { useAuth } from '@/lib/AuthContext';
import { toast } from 'sonner';

/**
 * AdminRoute — Double-enforcement admin guard.
 *
 * Layer A (instant): blocks based on cached isAdmin from AuthContext (no network).
 * Layer B (on mount): live re-fetches user role from the server.
 *   - If role was revoked mid-session, user is redirected immediately without needing to log out.
 *
 * The server enforces role independently — this is a UX defence-in-depth layer.
 */
export default function AdminRoute({ children }) {
  const { isAdmin, refreshAdminStatus } = useAuth();
  const navigate = useNavigate();
  const [verified, setVerified] = useState(false);

  useEffect(() => {
    // Mounted ref prevents setState / navigation on an unmounted component
    // (React StrictMode double-invokes effects; the cleanup fires before the second run)
    let mounted = true;

    // Layer A: instant cached check — kick out non-admins immediately
    if (!isAdmin) {
      toast.error('Access denied. Admin privileges required.');
      navigate(createPageUrl('Dashboard'), { replace: true });
      return () => { mounted = false; };
    }

    // Layer B: live server re-fetch — verify role hasn't been revoked server-side
    refreshAdminStatus().then((stillAdmin) => {
      if (!mounted) return;
      if (!stillAdmin) {
        toast.error('Access denied. Your admin privileges have been revoked.');
        navigate(createPageUrl('Dashboard'), { replace: true });
      } else {
        setVerified(true);
      }
    });

    return () => { mounted = false; };
    // refreshAdminStatus is intentionally omitted from deps: it is defined as a
    // plain arrow function in AuthContext (no useCallback) and changes reference
    // on every AuthProvider render.  Including it would cause an infinite loop
    // because refreshAdminStatus() itself triggers setUser() → re-render →
    // new reference → effect re-runs.  isAdmin + navigate cover all meaningful
    // state transitions; refreshAdminStatus is called at most once per isAdmin
    // change, which is the desired behaviour.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, navigate]);

  // Don't render children until both layers pass
  if (!verified) {
    return (
      <div className="min-h-screen bg-[#0F172A] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#F59E0B]"></div>
      </div>
    );
  }

  return children;
}
